// Updated event-modals/EventDetailsModal.js with smaller fonts and optimized date/time layout
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import EventDetailsTab from './EventDetailsTab';
import { updateEvent, fetchEventById } from '../../../../services/eventService';
import { useAuth } from '../../../../contexts/AuthContext';
import { useLanguage } from '../../../../contexts/LanguageContext';
import '../../../MobileModals.css';

function EventDetailsModal({ event, onClose, onSave }) {
    const { userCommunities } = useAuth();
    const { t } = useLanguage();
    const [error, setError] = useState('');
    const [eventData, setEventData] = useState({
        id: event?.id, // Include the ID for existing events
        title: event?.title || "",
        location: event?.location || "",
        event_type: event?.event_type || "rehearsal",
        time: event?.time ? event.time.slice(0, 5) : "09:00",
        description: event?.description || "",
        visibility: event?.visibility || "community",
        date: event?.date || "",
        community_id: event?.community_id || null,
        setlist_id: event?.setlist_id || null,
        owner_id: event?.owner_id || null, // Include the owner ID for permission checks
        owner_display_name: event?.owner_display_name || "",
        youtube_link: event?.youtube_link || ""
    });
    const [saving, setSaving] = useState(false);
    const [modalRoot, setModalRoot] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [setlistNeedsReset, setSetlistNeedsReset] = useState(false);
    const [previousCommunityId, setPreviousCommunityId] = useState(event?.community_id);
    
    // Handle community change
    const handleCommunityChange = (oldCommunityId, newCommunityId) => {
        // Make sure the community ID is an integer or null
        const parsedNewCommunityId = newCommunityId ? parseInt(newCommunityId) : null;

        // If community changed and there's a setlist
        if (oldCommunityId !== parsedNewCommunityId && eventData.setlist_id) {
            if (window.confirm(t('events.messages.changeCommunityWarning'))) {
                // Mark setlist for reset
                setSetlistNeedsReset(true);
                // Update the community ID and clear the setlist_id
                setEventData(prev => ({
                    ...prev,
                    community_id: parsedNewCommunityId,
                    setlist_id: null
                }));
            } else {
                // Revert community change
                setEventData(prev => ({
                    ...prev,
                    community_id: oldCommunityId
                }));
            }
        } else {
            // Just update the community ID
            setEventData(prev => ({
                ...prev,
                community_id: parsedNewCommunityId
            }));
        }
    };

    // Check for mobile device on resize and mount
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        window.addEventListener('resize', handleResize);
        handleResize(); // Initial check
        
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // Find or create the modal root element
    useEffect(() => {
        let root = document.getElementById('modal-root');
        if (!root) {
            root = document.createElement('div');
            root.id = 'modal-root';
            document.body.appendChild(root);
        }
        setModalRoot(root);

        // Clean up function
        return () => {
            if (root && root.childNodes.length === 0) {
                document.body.removeChild(root);
            }
        };
    }, []);

    const handleSave = async () => {
        // Validate required fields
        if (!eventData.title || !eventData.date || !eventData.location) {
            setError(t('events.messages.fillRequired'));
            return;
        }

        // If visibility is set to community but no community is selected, show error
        if (eventData.visibility === 'community' && !eventData.community_id) {
            setError(t('events.messages.selectCommunity'));
            return;
        }

        setSaving(true);
        setError('');

        try {
            // Make a copy of eventData for the API call
            const dataToSend = { ...eventData };

            // Ensure community_id is properly formatted (integer or null)
            dataToSend.community_id = dataToSend.community_id
                ? parseInt(dataToSend.community_id)
                : null;

            // Update the event with the new details
            await updateEvent(event.id, dataToSend);

            // If we changed communities and had a setlist, fetch the updated event to confirm setlist removal
            if (setlistNeedsReset && previousCommunityId !== eventData.community_id) {
                const refreshedEvent = await fetchEventById(event.id);
                // Set the previous community ID to the new one
                setPreviousCommunityId(refreshedEvent.community_id);
            }

            if (typeof onSave === 'function') {
                onSave();
            }
        } catch (error) {
            setError(t('events.messages.errorSaving') + ': ' + (error.message || t('events.messages.unknownError')));
        } finally {
            setSaving(false);
            setSetlistNeedsReset(false);
        }
    };

    // Trap focus within modal for accessibility
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        
        // Add body class to prevent scrolling
        document.body.classList.add('modal-open');
        
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.classList.remove('modal-open');
        };
    }, [onClose]);

    const getModalWidth = () => {
        if (isMobile) return '100%';
        
        // For desktop, use 2/3 of the viewport width, but capped at 1200px
        const viewportWidth = window.innerWidth;
        const twoThirdsWidth = Math.floor(viewportWidth * 0.66);
        return Math.min(twoThirdsWidth, 1200) + 'px';
    };

    // If modal root is not available yet, don't render anything
    if (!modalRoot) return null;

    // Use createPortal to render the modal outside the component hierarchy
    return createPortal(
        <div className="modal-overlay">
             <div className="modal-container" style={{ maxWidth: getModalWidth() }}>
                <div className="card border-0 h-100 d-flex flex-column">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
                        <h5 className="card-title mb-0" style={{ fontSize: isMobile ? '0.9rem' : '1.1rem' }}>
                            {t('events.modals.details.title')}
                        </h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>

                    <div className="card-body p-2 p-md-3 overflow-auto" style={{ maxHeight: isMobile ? 'calc(85vh - 120px)' : '500px' }}>
                        {error && 
                            <div className="alert alert-danger py-2 px-3" style={{ fontSize: isMobile ? '0.75rem' : '0.85rem' }}>
                                {error}
                            </div>
                        }
                        
                        <EventDetailsTab 
                            eventData={eventData} 
                            setEventData={setEventData}
                            isMobile={isMobile}
                            userCommunities={userCommunities}
                            onCommunityChange={handleCommunityChange}
                        />
                    </div>

                    <div className="card-footer bg-white d-flex justify-content-end py-2">
                        <button
                            type="button"
                            className={`btn btn-secondary me-2 ${isMobile ? 'btn-sm' : ''}`}
                            onClick={onClose}
                            disabled={saving}
                            style={{ fontSize: isMobile ? '0.75rem' : '0.85rem' }}
                        >
                            {t('events.modals.details.cancel')}
                        </button>
                        <button
                            type="button"
                            className={`btn btn-primary ${isMobile ? 'btn-sm' : ''}`}
                            onClick={handleSave}
                            disabled={saving}
                            style={{ fontSize: isMobile ? '0.75rem' : '0.85rem' }}
                        >
                            {saving ? t('events.modals.details.saving') : t('events.modals.details.save')}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        modalRoot
    );
}

export default EventDetailsModal;